import type { Client } from 'types/client';
import type { TicMeta } from 'types/tic';
import type { Cost, Duration } from 'types/units';
import type { Avatar } from 'types/user';
import type { Currency } from './account';
import type { Merge } from './utility';
export type RateType = 'project' | 'user' | 'non-billable';

export type BudgetType = 'H' | 'M' | '' | null;

export type BudgetScope = 'tag' | 'project' | null;

export type BudgetInterval = 'month' | 'week' | null;
export type BudgetRecurrenceEnd = 'end_date' | 'archived';
export type BudgetCalculationStatus = 'pending' | 'completed';

export enum TagSettings {
  All = 'all',
  None = 'none',
  Custom = 'custom',
}

export interface BudgetRecurrence {
  recur: BudgetInterval;
  start_date: string | null;
  end_date: string | null;
  recur_until: BudgetRecurrenceEnd;
}

export interface ProjectLabel {
  label_id: number;
  project_id: number;
  required: boolean;
  budget: number | null;
  updated_at: string;
  default: boolean;
}

export interface ProjectUser {
  user_id: number;
  deleted: boolean;
  hour_rate: number;
  hour_rate_in_cents: number;
}

export interface ProjectBase {
  id: number;
  client: Client;
  color: string | null;
  name: string;
  /** false means the project is archived */
  active: boolean;
  description?: string | null;

  /**
   * TIC (Timely Integration Cloud) metadata.
   * Only present for projects synced from TIC.
   * @see TicMeta
   */
  tic?: TicMeta;
}

export type ExchangeRate = [oldRate: number, newRate: number];

export interface Project extends ProjectBase {
  rate_type: RateType;
  billable: boolean;
  updated_at: number | string; // version=3 returns number, other payload are strings. yey.,
  label_ids: Array<number>;
  enable_labels: TagSettings;
  required_label_ids?: Array<number>;
  /** Require filling in note when logging to project */
  required_notes: boolean;
  /** Require at least one tag when logging to project */
  required_labels: boolean;
  labels: Array<ProjectLabel>;
  team_ids: Array<number>;
  external_id: number | string | null;
  allow_only_one_tag: boolean;
  budget: number | null;
  budget_type: BudgetType;
  budget_expired_on?: string | null;
  hour_rate: number;
  hour_rate_in_cents: number;
  budget_scope: BudgetScope;
  budget_recurrence: BudgetRecurrence;
  // Recurring budget to apply from the next period (TT-5786); saved with the project.
  upcoming_budget?: number | null;
  budget_percent: number;
  budget_progress: number;
  budget_calculation: BudgetCalculationStatus;
  /** Should invoices be based on time entries logged, or the budget specified? */
  invoice_by_budget: boolean;
  // &totals=true includes
  cost?: Cost;
  estimated_cost?: Cost;
  billed_cost?: Cost;
  unbilled_cost?: Cost;
  duration?: Duration;
  estimated_duration?: Duration;
  billed_duration?: Duration;
  unbilled_duration?: Duration;

  /**
   * Only available when project has been fethed singularly by id
   */
  users?: Array<ProjectUser>;
  first_logged_on?: string;
  last_logged_on?: string;

  currency: Currency;
  exchange_rate?: ExchangeRate;

  /** If true, the project has at least one entry marked as locked. */
  locked_hours?: boolean;

  /** If true, the project has default labels. */
  default_labels?: boolean;

  /** If `default_labels` is enabled, this contains the IDs of the default labels. */
  default_label_ids?: Array<number>;

  /**
   * If set to anything other than "Web", this will be used to display the import source for the
   * project, for example "ClickUp".
   * */
  created_from: string;
}

export interface LightProject extends ProjectBase {
  updated_at: string;
  created_at: string;
  has_permission?: boolean;
}

/**
 * Used to store two distinc types of Project in the store without rewriting or inserting
 * dummy data. This makes intent more clear and forces users of the state to decide explicitly
 * how to handle different levels of details for loaded projects; eg should we go ahead even
 * though we only have the light payload?
 *
 * Use as is to get Project or Lightproject, or use one of the utility functions in utils/projects.ts
 */
export type ProjectVariant =
  | { type: 'full'; project: Project }
  | { type: 'light'; lightProject: LightProject };

export type ProjectVariantMap = Record<number, ProjectVariant | undefined>;

/**
 * This type wraps around the uncertainty of ProjectVariant, which is great for storage
 * but not access, and adds fields for ease of access and enrichment.
 */
export interface EnrichedProject {
  base: ProjectBase;
  isFullyLoaded: boolean;
  isPinned?: boolean;
  fullProject: Project | null;
}

export interface ProjectCreateUpdatePayload {
  id?: number;
  name: string;
  description?: string | null;
  client_id: number;
  color: string | null;
  active?: boolean;
  budget?: number;
  team_ids?: Array<number>;
  budget_type?: BudgetType;
  budget_interval?: BudgetInterval;
  external_id?: string | number;
  hour_rate?: number;
  rate_type?: RateType;
  users?:
    | Array<{
        user_id: number | null | undefined;
        hour_rate?: number | null;
      }>
    | null
    | undefined;
  labels?: Array<ProjectLabel>;
  enable_labels: TagSettings;
  /** Require filling in note when logging to project */
  required_notes: boolean;
  /** Require at least one tag when logging to project */
  required_labels: boolean;
  /** Allow only one tag to be added to a time entry */
  allow_only_one_tag?: boolean;
  budget_recurrence?: BudgetRecurrence | null | undefined;
  /** Recurring budget to apply from the next period (TT-5786). */
  upcoming_budget?: number;
  billable?: boolean;
  invoice_by_budget?: boolean;
  update_existing_hours?: boolean;
  update_unbilled_only?: boolean;
  update_hour_billable_state?: boolean;
  update_hours_default_labels?: boolean;
  currency_code?: string;
  exchange_rate?: number;
  default_labels?: boolean;
}

export interface ProjectUserRelation {
  user_id: number;
  hour_rate: number | null;
}
export interface EnrichedProjectUserRelation extends ProjectUserRelation {
  name: string;
  avatar: Avatar;
  internal_hour_rate: number | null;
}

interface DraftExtension {
  id?: number;
  // Override nullable in Project
  users: Array<ProjectUserRelation>;
  // Override full Client object with this much smaller type
  client: {
    id: number | null;
    name: string | undefined;
    color: string;
  };

  update_hour_billable_state: boolean | null;
  update_hour_rates: ProjectRateChange | null;
  update_hour_default_labels: boolean | null;

  defaultTags?: Record<number, number>;
}

type IgnoredPropertiesInDraft = 'id' | 'account_id' | 'hour_rate_in_cents' | 'pinned';
export type Draft = Merge<Omit<Project, IgnoredPropertiesInDraft>, DraftExtension>;

interface EnrichedDraftExtension {
  users: Array<EnrichedProjectUserRelation>;
  selectedUsers: Array<number>;
}

export type EnrichedDraft = Merge<Draft, EnrichedDraftExtension>;

export interface EnrichedProjectWithBudget extends EnrichedProject {
  budgetAttributes: ProjectBudgetAttributes;
}

export interface ProjectBudgetAttributes {
  hasBudget: boolean;
  hasRecurringBudget: boolean;
  budgetValue: number;
  budgetSpend: number;
  budgetRemaining: number;
  budgetProgress: number;
  budgetIsMoney: boolean;
  budgetIsHours: boolean;
  budgetIsPending: boolean;
  budgetSpendInPercent: number;
  loggedMinutes: number;
  plannedMinutes: number;
  billedMinutes: number;
  unbilledMinutes: number;
  loggedMoney: number;
  plannedMoney: number;
  billedMoney: number;
  unbilledMoney: number;
}

export interface LightProjectWithRate extends LightProject {
  rate_type: RateType;
  rate: number | null | undefined;
  currency_code: string;
}

export interface LightProjectWithRateAndPermission extends LightProjectWithRate {
  has_permission: boolean;
}

export enum ProjectRateChange {
  NewOnly = 'new',
  UnbilledOnly = 'unbilled',
}

export type FilterGroupValues = 'all' | 'active' | 'archived';
